import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Landmark, TrendingUp, Wallet, Receipt, Activity,
  ShieldCheck, ArrowUpRight, ArrowDownRight,
} from "lucide-react";

/**
 * SERP Financial Command Center — animated hero preview.
 *
 * - KPI counters (Total Sales, Receivables, Payables) count up from 0 on mount.
 * - Live Day Book ticker scrolls simulated recent vouchers.
 * - Pulsing "Audit-Ready" status indicator in orange.
 * - Animated sparkline representing monthly cash flow trends.
 * - Deep Navy shell (#0F172A) with Orange accents (#FF5500).
 */

const KPIS = [
  { key: "sales",       label: "Total Sales",  target: 12_84_500, icon: TrendingUp,
    color: "#10B981", tint: "rgba(16, 185, 129, 0.20)" },
  { key: "receivables", label: "Receivables", target: 4_82_300, icon: ArrowDownRight,
    color: "#3B82F6", tint: "rgba(59, 130, 246, 0.20)" },
  { key: "payables",    label: "Payables",    target: 2_15_700, icon: ArrowUpRight,
    color: "#F59E0B", tint: "rgba(245, 158, 11, 0.20)" },
];

const TICKER_ROWS = [
  { kind: "SV", num: "SV-000482", party: "Blue Ridge Motors",   amt: "82,400",  dir: "cr" },
  { kind: "PV", num: "PV-000151", party: "Tara Fabrications",   amt: "37,650",  dir: "dr" },
  { kind: "RCT", num: "RCT-000097", party: "Kaveri Textiles",   amt: "1,15,000", dir: "cr" },
  { kind: "SV", num: "SV-000483", party: "Skyline AC Services", amt: "48,900",  dir: "cr" },
  { kind: "PAY", num: "PAY-000068", party: "SBI Business Loan", amt: "24,500",  dir: "dr" },
  { kind: "JC", num: "JC-00214",   party: "MH-12 AB 1234 · Service", amt: "5,850", dir: "cr" },
  { kind: "SV", num: "SV-000484", party: "Nirmala Constructions", amt: "3,45,000", dir: "cr" },
  { kind: "PV", num: "PV-000152", party: "Godrej Interio",       amt: "1,08,200", dir: "dr" },
];

const CASH_FLOW_POINTS = [42, 58, 51, 74, 66, 82, 95, 88, 104, 118, 132, 145];

function fmtINR(n) {
  return n.toLocaleString("en-IN");
}

function useCountUp(target, duration = 1600, delay = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    let started = false;
    const start = performance.now() + delay;
    const step = (now) => {
      const t = Math.max(0, now - start);
      const progress = Math.min(1, t / duration);
      // easeOutQuint
      const eased = 1 - Math.pow(1 - progress, 5);
      setVal(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(step);
      started = true;
    };
    raf = requestAnimationFrame(step);
    return () => { if (raf && started) cancelAnimationFrame(raf); };
  }, [target, duration, delay]);
  return val;
}


function KpiTile({ item, delay }) {
  const v = useCountUp(item.target, 1600, delay);
  const Icon = item.icon;
  return (
    <div
      className="relative overflow-hidden rounded-xl p-3 flex flex-col gap-1"
      style={{
        background: "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
        border: "1px solid rgba(255,255,255,0.10)",
      }}
      data-testid={`fcc-kpi-${item.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{item.label}</div>
        <div className="w-6 h-6 rounded-md flex items-center justify-center"
              style={{ background: item.tint, color: item.color }}>
          <Icon size={13} strokeWidth={2}/>
        </div>
      </div>
      <div className="font-mono-data text-lg font-bold text-white leading-none" data-testid={`fcc-kpi-${item.key}-value`}>
        ₹{fmtINR(v)}
      </div>
      <div className="text-[9px] text-slate-500 font-mono-data leading-none mt-0.5">
        this month
      </div>
      <div className="absolute -bottom-2 -right-2 w-14 h-14 rounded-full opacity-25"
             style={{ background: `radial-gradient(circle, ${item.color} 0%, transparent 70%)` }}/>
    </div>
  );
}


function Sparkline({ points, color = "#FF5500" }) {
  const width = 220;
  const height = 60;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const path = points.map((p, i) => {
    const x = i * step;
    const y = height - ((p - min) / range) * (height - 8) - 4;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  const area = `${path} L ${width} ${height} L 0 ${height} Z`;
  const last = points[points.length - 1];
  const first = points[0];
  const delta = Math.round(((last - first) / first) * 100);
  return (
    <div className="rounded-xl p-3"
          style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
                    border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="fcc-sparkline">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Cash Flow · 12mo</div>
          <div className="text-white font-bold font-mono-data text-sm mt-0.5">₹{fmtINR(last * 1000)}</div>
        </div>
        <div className="chip chip-success text-[10px]" style={{ background: "rgba(16, 185, 129, 0.15)",
                                                                       borderColor: "rgba(16, 185, 129, 0.35)",
                                                                       color: "#34D399" }}>
          <TrendingUp size={10}/> +{delta}%
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-14 block"
             preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="fcc-spark-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.55"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={area} fill="url(#fcc-spark-grad)"/>
        <path d={path} fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                style={{
                  strokeDasharray: 800,
                  strokeDashoffset: 800,
                  animation: "fcc-draw 1.6s ease-out 300ms forwards",
                }}/>
        {points.map((p, i) => {
          const x = i * step;
          const y = height - ((p - min) / range) * (height - 8) - 4;
          if (i === points.length - 1) {
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="6" fill={color} opacity="0.25">
                  <animate attributeName="r" from="3" to="8"
                             dur="1.4s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" from="0.5" to="0"
                             dur="1.4s" repeatCount="indefinite"/>
                </circle>
                <circle cx={x} cy={y} r="3" fill={color}/>
              </g>
            );
          }
          return null;
        })}
      </svg>
    </div>
  );
}


function DayBookTicker() {
  const trackRef = useRef(null);
  const [rows] = useState(() => [...TICKER_ROWS, ...TICKER_ROWS]); // duplicate for seamless loop
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
                border: "1px solid rgba(255,255,255,0.10)" }}
      data-testid="fcc-daybook"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-2">
          <Activity size={12} className="text-[#FF7A33]"/>
          <span className="text-[10px] uppercase tracking-widest text-slate-300 font-semibold">Day Book · Live</span>
        </div>
        <span className="flex items-center gap-1 text-[9px] text-slate-500 font-mono-data">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"
                  style={{ animation: "fcc-blink 1.4s ease-in-out infinite" }}/>
          streaming
        </span>
      </div>
      <div className="relative h-40 overflow-hidden">
        <div ref={trackRef}
              className="flex flex-col text-xs font-mono-data"
              style={{ animation: "fcc-scroll 26s linear infinite" }}>
          {rows.map((r, i) => (
            <div key={i}
                  className="flex items-center gap-2 px-3 py-2 border-b"
                  style={{ borderColor: "rgba(255,255,255,0.04)" }}>
              <span className="chip text-[9px]"
                    style={{ background: "rgba(255,85,0,0.12)",
                              borderColor: "rgba(255,85,0,0.35)",
                              color: "#FF9966",
                              padding: "1px 6px" }}>
                {r.kind}
              </span>
              <span className="text-slate-400">{r.num}</span>
              <span className="text-white flex-1 truncate">{r.party}</span>
              <span className={r.dir === "cr" ? "text-emerald-400" : "text-amber-400"}>
                {r.dir === "cr" ? "+" : "-"}₹{r.amt}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function AuditReadyBadge() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full px-3 py-1"
          style={{ background: "rgba(255,85,0,0.15)",
                    border: "1px solid rgba(255,85,0,0.4)" }}
          data-testid="fcc-audit-ready">
      <span className="relative flex w-2.5 h-2.5">
        <span className="absolute inline-flex w-full h-full rounded-full bg-[#FF5500] opacity-75"
                style={{ animation: "fcc-ping 1.6s cubic-bezier(0,0,0.2,1) infinite" }}/>
        <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-[#FF5500]"/>
      </span>
      <ShieldCheck size={12} className="text-[#FF9966]"/>
      <span className="text-[10px] uppercase tracking-widest font-semibold text-[#FF9966]">
        Audit-Ready
      </span>
    </div>
  );
}


export default function FinancialCommandCenter() {
  // Inject keyframes once
  useEffect(() => {
    const id = "fcc-keyframes";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      @keyframes fcc-scroll { 0% { transform: translateY(0); } 100% { transform: translateY(-50%); } }
      @keyframes fcc-draw   { to { stroke-dashoffset: 0; } }
      @keyframes fcc-ping   { 75%, 100% { transform: scale(2); opacity: 0; } }
      @keyframes fcc-blink  { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      @keyframes fcc-glow   { 0%, 100% { box-shadow: 0 0 0 rgba(255,85,0,0); }
                               50%     { box-shadow: 0 0 24px rgba(255,85,0,0.35); } }
    `;
    document.head.appendChild(s);
  }, []);

  const health = useMemo(() => 96, []); // static demo score

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-2xl"
      style={{
        background: "linear-gradient(160deg, #0F172A 0%, #1E293B 55%, #0F172A 100%)",
        border: "1px solid rgba(255,85,0,0.18)",
        animation: "fcc-glow 4s ease-in-out infinite",
      }}
      data-testid="landing-fcc"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle, #FF5500 0%, transparent 60%)" }}/>
      <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full opacity-20 pointer-events-none"
             style={{ background: "radial-gradient(circle, #3B82F6 0%, transparent 60%)" }}/>

      <div className="relative p-4 sm:p-5 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#FF7A33] font-semibold">
              Financial Command Center
            </div>
            <div className="text-white text-base font-bold mt-0.5 flex items-center gap-2">
              Your Enterprise Dashboard
            </div>
          </div>
          <AuditReadyBadge />
        </div>

        {/* KPI grid — animated count-up */}
        <div className="grid grid-cols-3 gap-2">
          {KPIS.map((k, i) => <KpiTile key={k.key} item={k} delay={i * 120}/>)}
        </div>

        {/* Sparkline + Health */}
        <div className="grid grid-cols-5 gap-2">
          <div className="col-span-3">
            <Sparkline points={CASH_FLOW_POINTS}/>
          </div>
          <div className="col-span-2 rounded-xl p-3 flex flex-col justify-between"
                style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
                          border: "1px solid rgba(255,255,255,0.10)" }}
                data-testid="fcc-health-card">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Books Health</div>
              <div className="mt-1 font-mono-data text-2xl font-bold text-white">{health}<span className="text-sm text-slate-400">/100</span></div>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-400">
              <div className="flex-1 h-1.5 rounded-full overflow-hidden"
                    style={{ background: "rgba(255,255,255,0.08)" }}>
                <div className="h-full rounded-full"
                        style={{
                          background: "linear-gradient(90deg, #FF5500, #10B981)",
                          width: `${health}%`,
                          animation: "fcc-draw 1.4s ease-out",
                        }}/>
              </div>
            </div>
          </div>
        </div>

        {/* Day Book ticker */}
        <DayBookTicker/>

        {/* Icon rail — Bank / Sales / Expenses */}
        <div className="grid grid-cols-3 gap-2 pt-1">
          {[
            { Icon: Landmark, label: "Bank",     tone: "#3B82F6" },
            { Icon: Receipt,  label: "Sales",    tone: "#10B981" },
            { Icon: Wallet,   label: "Expenses", tone: "#F59E0B" },
          ].map((s) => (
            <div key={s.label}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
                  style={{ background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid={`fcc-icon-${s.label.toLowerCase()}`}>
              <div className="w-6 h-6 rounded-md flex items-center justify-center"
                    style={{ background: `${s.tone}20`, color: s.tone }}>
                <s.Icon size={13} strokeWidth={1.8}/>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-slate-300 font-semibold">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
